import { Link } from 'react-router-dom';

const TIERS = [
  {
    name: 'Light',
    who: '2G · feature-grade Android · screen readers',
    what: 'Objective, transcript, static images, and every question as a plain form control.',
  },
  {
    name: 'Standard',
    who: 'Most phones and school computers',
    what: 'SVG scenes, draggable bar graphs, interactive frequency tables, pie charts.',
  },
  {
    name: 'Rich',
    who: 'Newer devices on good connections',
    what: 'Layered scenes with depth, animated arrivals, magnified graph regions.',
  },
  {
    name: 'Full 3D',
    who: 'Desktop and high-end tablets',
    what: 'Three-dimensional apparatus for physics, chemistry and engineering.',
  },
];

const AVAILABLE = [
  {
    subject: 'Mathematics — Grade 7',
    title: 'Understanding Data Handling',
    body: 'Collect data at five real sites, build frequency distribution tables, plot pictographs, bar graphs, line graphs and pie charts, then read them to answer questions.',
  },
];

const PLANNED = [
  'Physics', 'Chemistry', 'Biology', 'Home Science', 'Health Science', 'Computer Science',
  'Integrated Science', 'Agriculture', 'Drawing and Design', 'Aviation Technology',
  'Building and Construction', 'Electrical Technology', 'Metal Technology', 'Power Mechanics',
  'Wood Technology', 'Media Technology', 'Marine Technology', 'Engineering', 'Coding',
  'Robotics', 'Artificial Intelligence', 'Environmental Science',
];

export function LaboratoriesPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-14">
      <h1 className="text-3xl font-bold text-ink-900">Virtual STEM Laboratory</h1>
      <p className="mt-3 max-w-2xl text-ink-700">
        Interactive practical experiments, with assessment, laboratory reports, a teacher dashboard,
        learner progress tracking and digital laboratory certificates.
      </p>

      <section className="mt-10">
        <h2 className="text-xl font-semibold text-ink-900">Available now</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {AVAILABLE.map(lab => (
            <article key={lab.title} className="card">
              <p className="text-xs font-medium uppercase tracking-wide text-brand-700">
                {lab.subject}
              </p>
              <h3 className="mt-1 text-lg font-semibold text-ink-900">{lab.title}</h3>
              <p className="mt-1 text-sm text-ink-600">{lab.body}</p>
              <Link to="/login" className="btn-primary mt-4">
                Sign in to open it
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="text-xl font-semibold text-ink-900">One laboratory, four levels of detail</h2>
        <p className="mt-2 max-w-2xl text-sm text-ink-700">
          Each laboratory is a single declarative script. The runtime chooses how much to draw from
          the learner's connection, device and accessibility preferences — and they can override it.
          <strong className="font-semibold">
            {' '}
            The questions are byte-identical at every level.
          </strong>{' '}
          That is what makes the lightest version a real experience rather than a consolation prize.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map(tier => (
            <div key={tier.name} className="card">
              <h3 className="text-sm font-semibold text-ink-900">{tier.name}</h3>
              <p className="mt-0.5 text-xs text-ink-500">{tier.who}</p>
              <p className="mt-2 text-sm text-ink-700">{tier.what}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="text-xl font-semibold text-ink-900">In development</h2>
        <p className="mt-1 text-sm text-ink-600">
          Twenty-two laboratories are planned. Because the engine is one interpreter over a script,
          each is authoring work rather than a new application.
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {PLANNED.map(subject => (
            <span key={subject} className="rounded-full bg-ink-100 px-3 py-1 text-xs text-ink-600">
              {subject}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
